import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';


export default function CookiePolicy() {

    return (
        <div className="min-h-screen bg-background py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto bg-card p-8 rounded-lg shadow border">
                <div className="mb-6">
                    <Button
                        variant="ghost"
                        onClick={() => window.history.back()}
                        style={{ paddingLeft: "0" }}
                        className="text-muted-foreground hover:text-foreground"
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back
                    </Button>
                </div>
                <div className="prose prose-slate dark:prose-invert max-w-none">
                    <h1 className="text-3xl font-bold text-foreground mb-4">Cookie Policy</h1>
                    <p className="text-sm text-muted-foreground mb-8">
                        <strong>Effective date:</strong> 01 April 2026
                    </p>

                    <div className="space-y-6 text-foreground/80">
                        <p>
                            This Cookie Policy explains how <strong>Source Code Control</strong> (“we”, “our”, or “us”) uses cookies and similar technologies when you visit or use <strong>Orbit8</strong> at:
                        </p>
                        <p className="font-semibold text-primary">orbit8.dvayweb.com</p>
                        <p>This policy should be read together with our Privacy Policy.</p>

                        <hr className="my-8 border-border" />

                        <section id="what-are-cookies">
                            <h2 className="text-2xl font-semibold text-foreground">1. What Are Cookies?</h2>
                            <p>Cookies are small text files placed on your device when you visit a website. They help websites function properly, improve user experience, and provide usage insights.</p>
                            <p className="mt-2">Cookies may be:</p>
                            <ul className="list-disc pl-6 space-y-1">
                                <li><strong>First-party cookies</strong> — set by us</li>
                                <li><strong>Third-party cookies</strong> — set by external service providers</li>
                                <li><strong>Session cookies</strong> — deleted when you close your browser</li>
                                <li><strong>Persistent cookies</strong> — remain for a defined period</li>
                            </ul>
                        </section>

                        <hr className="my-8 border-border" />

                        <section id="how-we-use">
                            <h2 className="text-2xl font-semibold text-foreground">2. How We Use Cookies</h2>
                            <p>Orbit8 uses cookies and similar technologies for the following purposes:</p>
                            <ul className="list-disc pl-6 space-y-1">
                                <li>To enable secure login and session management</li>
                                <li>To remember user preferences</li>
                                <li>To understand how the platform is used and improve performance</li>
                                <li>To maintain security and detect abuse</li>
                            </ul>
                            <p className="mt-4">We do <strong>not</strong> use cookies to directly identify you as an individual.</p>
                        </section>

                        <hr className="my-8 border-border" />

                        <section id="types">
                            <h2 className="text-2xl font-semibold text-foreground">3. Types of Cookies We May Use</h2>
                            <p>Because Orbit8 is an authenticated SaaS platform, the following categories of cookies may be used.</p>

                            <div className="mt-6 space-y-4">
                                <div>
                                    <h3 className="text-xl font-medium text-foreground">Strictly Necessary Cookies</h3>
                                    <p>These cookies are required for the platform to function and cannot be switched off. They are used to:</p>
                                    <ul className="list-disc pl-6 space-y-1">
                                        <li>Maintain user login sessions</li>
                                        <li>Provide secure authentication</li>
                                        <li>Enable core platform functionality</li>
                                        <li>Ensure platform security</li>
                                    </ul>
                                    <p className="mt-2 text-sm italic">Legal basis (GDPR): Legitimate interest / necessary for service delivery.</p>
                                </div>

                                <hr className="my-4 border-border/50" />

                                <div>
                                    <h3 className="text-xl font-medium text-foreground">Performance and Analytics Cookies</h3>
                                    <p>These cookies help us understand how Orbit8 is used so we can improve performance and usability. They may collect information such as:</p>
                                    <ul className="list-disc pl-6 space-y-1">
                                        <li>Pages visited</li>
                                        <li>Feature usage</li>
                                        <li>Error rates</li>
                                        <li>General usage patterns</li>
                                    </ul>
                                    <p className="mt-2">These cookies are typically aggregated and do not directly identify users.</p>
                                </div>

                                <hr className="my-4 border-border/50" />

                                <div>
                                    <h3 className="text-xl font-medium text-foreground">Preference Cookies</h3>
                                    <p>These cookies remember choices you make, such as:</p>
                                    <ul className="list-disc pl-6 space-y-1">
                                        <li>UI preferences</li>
                                        <li>Language settings</li>
                                        <li>Display options</li>
                                    </ul>
                                    <p className="mt-2">They improve your experience but are not strictly necessary.</p>
                                </div>

                                <hr className="my-4 border-border/50" />

                                <div>
                                    <h3 className="text-xl font-medium text-foreground">Security and Integrity Cookies</h3>
                                    <p>Orbit8 may use cookies or similar technologies to:</p>
                                    <ul className="list-disc pl-6 space-y-1">
                                        <li>Detect suspicious activity</li>
                                        <li>Prevent fraud or abuse</li>
                                        <li>Maintain platform integrity</li>
                                        <li>Support secure infrastructure services</li>
                                    </ul>
                                </div>
                            </div>
                        </section>

                        <hr className="my-8 border-border" />

                        <section id="third-party">
                            <h2 className="text-2xl font-semibold text-foreground">4. Third-Party Cookies</h2>
                            <p>Orbit8 may use trusted third-party services to support analytics, hosting, monitoring, or security.</p>
                            <p>Where such services set cookies on our behalf, they are contractually required to process data in accordance with applicable data protection laws.</p>
                            <p className="mt-2 text-sm font-medium">Important: A detailed cookie list will be updated as our implementation evolves.</p>
                        </section>

                        <hr className="my-8 border-border" />

                        <section id="rights">
                            <h2 className="text-2xl font-semibold text-foreground">5. Your Choices and Controls</h2>
                            <p>Depending on your location, you may have the right to control non-essential cookies.</p>
                            <p>You can manage cookies by:</p>
                            <ul className="list-disc pl-6 space-y-1">
                                <li>Adjusting your browser settings</li>
                                <li>Using our cookie consent banner (where applicable)</li>
                                <li>Deleting stored cookies at any time</li>
                            </ul>
                            <p className="mt-4 text-sm bg-muted p-3 rounded">Please note that disabling strictly necessary cookies may impact Orbit8 functionality, including login.</p>
                        </section>

                        <hr className="my-8 border-border" />

                        <section id="international">
                            <h2 className="text-2xl font-semibold text-foreground">6. International Users</h2>
                            <p>Orbit8 serves users globally, including the UK, EEA, India, and other regions. We aim to align our cookie practices with applicable laws including:</p>
                            <ul className="list-disc pl-6 space-y-1">
                                <li>UK GDPR</li>
                                <li>EU GDPR</li>
                                <li>ePrivacy requirements (where applicable)</li>
                            </ul>
                        </section>

                        <hr className="my-8 border-border" />

                        <section id="updates">
                            <h2 className="text-2xl font-semibold text-foreground">7. Updates to This Policy</h2>
                            <p>We may update this Cookie Policy from time to time to reflect:</p>
                            <ul className="list-disc pl-6 space-y-1">
                                <li>Changes in technology</li>
                                <li>Changes in legal requirements</li>
                                <li>Changes in our services</li>
                            </ul>
                            <p className="mt-2">When we make material changes, we will update the “Effective date” above.</p>
                        </section>

                        <hr className="my-8 border-border" />

                        <section id="contact">
                            <h2 className="text-2xl font-semibold text-foreground">8. Contact Us</h2>
                            <p>If you have questions about our use of cookies, please contact us at:</p>
                            <div className="bg-muted p-4 rounded-md mt-2">
                                <p className="font-bold">Source Code Control Limited</p>
                                <p>Email: <a href="mailto:open@sourcecodecontrol.com" className="text-primary hover:underline">open@sourcecodecontrol.com</a></p>
                            </div>
                        </section>
                    </div>
                </div>
            </div>
        </div>
    );
}